import { create } from 'zustand';

import { API, getRequest, postRequest, putRequest } from '@/api/api.ts';
import { imageRefusalMessage } from '@/common/error.ts';
import { useAuth } from '@/store/auth/auth.store';
import { getRoomMessages } from '@/store/chats/chat.api.ts';
import type {
  ChatMessage,
  PinnedMessagesResponse,
  RoomMessagesResponse,
} from '@/store/messages/messages.dto.ts';
import { useWebSocket } from '@/store/websocket/websocket.store';
import type { CursorPage } from '@/utils/list.ts';
import { randomId } from '@/utils/random-id.ts';
import { toast } from '@/utils/toast.ts';
import { isVideoAttachment } from '@/utils/video-attachments.ts';

type UploadFileOptions = {
  /** Mention text → mentioned user id, as the plain-text send passes them. */
  mentions?: Record<string, string>;
  quoteMessageId?: string;
};

type MessagesState = {
  messages: RoomMessagesResponse | null;
  messagesByRoom: Record<string, RoomMessagesResponse>;
  /** Where each room's stream continues; null once its oldest message is loaded. */
  cursorByRoom: Record<string, null | string>;
  bidMessages: RoomMessagesResponse | null;
  pinnedMessages: PinnedMessagesResponse | null;
  /**
   * Loads the newest step of a room's messages, or with `append` the step older
   * than what is loaded — resolved from the cursor the previous step left.
   */
  getMessages: (
    roomId: string,
    limit?: number,
    append?: boolean,
  ) => Promise<CursorPage<ChatMessage> | undefined>;
  getBidMessages: (roomId: string, limit?: number) => Promise<ChatMessage[] | undefined>;
  getAllMessages: (roomId: string) => Promise<void>;
  searchMessages: (roomId: string, text: string) => Promise<ChatMessage[] | undefined>;
  getPinnedMessages: (roomId: string) => Promise<void>;
  pinMessages: (roomId: string, messageId: string) => Promise<void>;
  pinMessagesInBroadcast: (roomId: string, messageId: string) => Promise<void>;
  unpinMessage: (roomId: string, messageId: string) => Promise<void>;
  unpinMessageInBroadcast: (roomId: string, messageId: string) => Promise<void>;
  sendMessage: (roomId: string, text: string) => Promise<void>;
  /** Sends every file, with the text, as one message. */
  uploadFiles: (
    roomId: string,
    files: File[],
    text: string,
    options?: UploadFileOptions,
  ) => Promise<void>;
  /**
   * Sends each file as its own message, in the order given. The text, mentions and reply
   * ride on the first one, so a caption reads above the files it introduces.
   */
  uploadFilesSeparately: (
    roomId: string,
    files: File[],
    text: string,
    options?: UploadFileOptions,
  ) => Promise<void>;
  clearMessages: () => void;
  markMessagesReadLocally: (roomId: string, messageIds: string[]) => void;
  error: string | null;
};

/** Over this many bytes in one upload, the long timeout applies even without a video. */
const LONG_UPLOAD_BYTES = 15 * 1024 * 1024;

const toError = (err: unknown) => (err instanceof Error ? err.message : String(err));

export const useMessages = create<MessagesState>((set) => ({
  messages: null,
  messagesByRoom: {},
  cursorByRoom: {},
  bidMessages: null,
  pinnedMessages: null,
  error: null,
  getMessages: async (roomId: string, limit = 100, append = false) => {
    try {
      const cursor = append ? useMessages.getState().cursorByRoom[roomId] : '';
      // Appending past the oldest message: nothing left to ask for.
      if (cursor === null) {
        return { items: [], hasMore: false, nextCursor: null };
      }

      const page = await getRoomMessages(roomId, limit, cursor ?? '');
      const roomMessages = page.items;

      set((state) => {
        const prevRoom = state.messagesByRoom[roomId];
        let nextRoom = roomMessages;
        if (append && prevRoom) {
          const seen = new Set(prevRoom.map((m) => m.id));
          nextRoom = [...prevRoom, ...roomMessages.filter((msg) => !seen.has(msg.id))];
        }
        const messagesByRoom = { ...state.messagesByRoom, [roomId]: nextRoom };
        const cursorByRoom = {
          ...state.cursorByRoom,
          [roomId]: page.hasMore ? page.nextCursor : null,
        };

        if (append && state.messages) {
          const existingIds = new Set(state.messages.map((msg) => msg.id));
          const newMessages = roomMessages.filter((msg) => !existingIds.has(msg.id));
          return {
            messages: [...state.messages, ...newMessages],
            messagesByRoom,
            cursorByRoom,
            error: null,
          };
        }
        return {
          messages: roomMessages,
          messagesByRoom,
          cursorByRoom,
          error: null,
        };
      });
      return page;
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  getBidMessages: async (roomId: string, limit = 100) => {
    try {
      const { items } = await getRoomMessages(roomId, limit);
      set({ bidMessages: items, error: null });
      return items;
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  getAllMessages: async (roomId: string) => {
    try {
      const allMessages: ChatMessage[] = [];
      let cursor = '';

      for (;;) {
        const page = await getRoomMessages(roomId, 100, cursor);
        allMessages.push(...page.items);
        if (!page.hasMore || !page.nextCursor) break;
        cursor = page.nextCursor;
      }

      set((state) => ({
        messages: allMessages,
        cursorByRoom: { ...state.cursorByRoom, [roomId]: null },
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  searchMessages: async (roomId: string, text: string) => {
    try {
      const { items } = await getRoomMessages(roomId, 100, '', { text });

      return items;
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  getPinnedMessages: async (roomId: string) => {
    try {
      const response = await getRequest<PinnedMessagesResponse>(
        API.CHAT,
        `/rooms/${roomId}/pinned`,
      );

      set({
        pinnedMessages: response.data,
        error: null,
      });
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  pinMessages: async (roomId: string, messageId: string) => {
    try {
      await putRequest<void, { message_id: string; pin: boolean }>(
        API.CHAT,
        `/rooms/${roomId}/pin`,
        { message_id: messageId, pin: true },
      );

      await useMessages.getState().getPinnedMessages(roomId);
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  pinMessagesInBroadcast: async (roomId: string, messageId: string) => {
    try {
      await putRequest<void, { message_id: string; pin: boolean }>(
        API.CHAT,
        `/rooms/${roomId}/pin/broadcast`,
        { message_id: messageId, pin: true },
      );

      await useMessages.getState().getPinnedMessages(roomId);
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  sendMessage: async (roomId: string, text: string) => {
    try {
      await postRequest<{ text: string }, { text: string }>(API.CHAT, `/rooms/${roomId}/messages`, {
        text,
      });

      await useMessages.getState().getMessages(roomId, 100, false);
      set({ error: null });
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  uploadFiles: async (
    roomId: string,
    files: File[],
    text: string,
    { mentions, quoteMessageId }: UploadFileOptions = {},
  ) => {
    if (files.length === 0) return;

    const tid = randomId();
    const localUrls = files.map((file) => URL.createObjectURL(file));
    const creatorId = useAuth.getState().session?.id ?? '';
    useWebSocket.getState().addFilePendingMessage(tid, roomId, files, text, creatorId, localUrls);

    const formData = new FormData();
    files.forEach((file) => formData.append('file', file));
    formData.append('text', text);
    // The upload endpoint takes the same mentions and reply the plain-text
    // send does, so an attachment carries them too.
    if (mentions && Object.keys(mentions).length > 0) {
      formData.append('mentions', JSON.stringify(mentions));
    }
    if (quoteMessageId) formData.append('quote_message_id', quoteMessageId);

    // A 100 MB video, or a handful of large files, never finishes inside the
    // default 15-second timeout.
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const isLongUpload =
      totalBytes > LONG_UPLOAD_BYTES ||
      files.some((file) => isVideoAttachment(file.type, file.name));
    const noun = files.length === 1 ? 'the file' : 'the files';

    try {
      await postRequest<FormData, FormData>(
        API.CHAT,
        `/rooms/${roomId}/messages/files`,
        formData,
        isLongUpload,
      );
    } catch (err) {
      // The bubble stays in the thread marked "Failed", the way a text message
      // that didn't make it does. Dropping it here is what made a failed upload
      // look like the message had simply vanished. Its previews stay alive for
      // the same reason.
      console.error('[chat] file upload failed', err);
      useWebSocket.getState().markMessageFailed(tid);
      set({ error: toError(err) });

      const message = String((err as { error_message?: unknown })?.error_message ?? '');
      const refusal = imageRefusalMessage(err);
      toast.error(
        refusal
          ? `Couldn’t upload ${files.length === 1 ? 'the image' : 'the images'}. ${refusal}`
          : message.includes('too long')
            ? `Couldn’t upload ${noun}. Please shorten ${files.length === 1 ? 'its name' : 'their names'}.`
            : `Couldn’t upload ${noun}. Please check your connection.`,
      );
      return;
    }

    try {
      await useMessages.getState().getMessages(roomId, 100, false);
      set({ error: null });
    } catch (err) {
      set({ error: toError(err) });
    } finally {
      localUrls.forEach((url) => URL.revokeObjectURL(url));
      useWebSocket.getState().removePendingMessage(tid);
    }
  },
  uploadFilesSeparately: async (roomId, files, text, options) => {
    const { uploadFiles } = useMessages.getState();
    // One after another rather than together: the service stamps each message as it lands,
    // and parallel uploads would put the files in the thread in whatever order they finish.
    // A failed one is marked in the thread by `uploadFiles` and does not stop the rest.
    for (const [index, file] of files.entries()) {
      await uploadFiles(roomId, [file], index === 0 ? text : '', index === 0 ? options : {});
    }
  },
  clearMessages: () => {
    set({ messages: null });
  },
  markMessagesReadLocally: (roomId: string, messageIds: string[]) => {
    const ids = new Set(messageIds);

    set((state) => {
      const room = state.messagesByRoom[roomId];
      if (!room) return state;

      return {
        messagesByRoom: {
          ...state.messagesByRoom,
          [roomId]: room.map((message) =>
            ids.has(message.id) && !message.read ? { ...message, read: true } : message,
          ),
        },
      };
    });
  },
  unpinMessage: async (roomId: string, messageId: string) => {
    try {
      await putRequest<void, { message_id: string; pin: boolean }>(
        API.CHAT,
        `/rooms/${roomId}/pin`,
        { message_id: messageId, pin: false },
      );

      await useMessages.getState().getPinnedMessages(roomId);
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  unpinMessageInBroadcast: async (roomId: string, messageId: string) => {
    try {
      await putRequest<void, { message_id: string; pin: boolean }>(
        API.CHAT,
        `/rooms/${roomId}/pin/broadcast`,
        { message_id: messageId, pin: false },
      );

      await useMessages.getState().getPinnedMessages(roomId);
    } catch (err) {
      set({ error: toError(err) });
    }
  },
}));
